import type { InferenceGeography, ModelRole } from "../models.ts";
import { CANONICAL_MODELS } from "./canonical.generated.ts";
import type { CanonicalModel } from "./canonical.ts";
import { meetsRoleNeeds } from "./capabilities.ts";
import {
  compareParts,
  DEFAULT_LABS,
  type DerivationScope,
  effortFamily,
  type FamilyId,
  isFamilyId,
  MODEL_FAMILIES,
  type ModelFamily,
  neighbourFamily,
  parsedVersion,
  ROLE_EFFORT_TIER,
} from "./families.ts";
import { BEDROCK_OFFERINGS, type BedrockOffering, type OfferingKey } from "./offerings.ts";

/**
 * Versions of the catalogued offerings (openspec change `model-picking`, D3, D4): each offering's
 * lab and family, version order, latest-eligibility and the candidates a role derives. Split from
 * `families.ts`, the curated data, because the offerings are assembled from it (slice 4b).
 */

/** The two catalogue layers version order reads; tests pass their own (a beta route…). */
export interface CatalogueLayers {
  readonly offerings: Readonly<Record<string, BedrockOffering>>;
  readonly canonical: Readonly<Record<string, CanonicalModel>>;
}

const CATALOGUE: CatalogueLayers = { offerings: BEDROCK_OFFERINGS, canonical: CANONICAL_MODELS };

/** `INFERENCE_GEOGRAPHIES`, without importing `models.ts` (which re-exports this module). */
const SERVED_GEOGRAPHIES: readonly InferenceGeography[] = ["eu", "global"];

function canonicalOf(key: string, layers: CatalogueLayers = CATALOGUE): CanonicalModel | undefined {
  const offering = layers.offerings[key];
  return offering ? layers.canonical[offering.canonical] : undefined;
}

/** The family ID of an offering (`<lab>/<canonical family>`); undefined without a family. */
export function familyIdOf(key: string, layers: CatalogueLayers = CATALOGUE): string | undefined {
  const canonical = canonicalOf(key, layers);
  return canonical?.family ? `${canonical.lab}/${canonical.family}` : undefined;
}

/** The curated family of an offering; undefined when it has none (the drift test refuses it). */
export function familyOf(key: string): ModelFamily | undefined {
  const id = familyIdOf(key);
  return id !== undefined && isFamilyId(id) ? MODEL_FAMILIES[id] : undefined;
}

/** The lab of an offering (its canonical `lab`). */
export function labOf(key: string): string | undefined {
  return canonicalOf(key)?.lab;
}

/**
 * Newest first (D3, Q2): by canonical release date, ties broken by the parsed version; a model
 * without a release date sorts after those with one.
 */
export function compareVersionsNewestFirst(
  a: string,
  b: string,
  layers: CatalogueLayers = CATALOGUE,
): number {
  const ca = canonicalOf(a, layers);
  const cb = canonicalOf(b, layers);
  const da = ca?.releaseDate ?? "";
  const db = cb?.releaseDate ?? "";
  if (da !== db) {
    return da < db ? 1 : -1;
  }
  return -compareParts(
    parsedVersion(ca?.id ?? a, ca?.family),
    parsedVersion(cb?.id ?? b, cb?.family),
  );
}

/** The route an offering uses in a geography: its profile there (none: not routable there). */
export function profileIn(
  key: string,
  geography: InferenceGeography,
  layers: CatalogueLayers = CATALOGUE,
): string | undefined {
  return layers.offerings[key]?.routes.profiles[geography];
}

/**
 * Whether an offering may be a family's newest version in a geography (D3): it routes there,
 * its route is neither `alpha` nor `beta` upstream (pinnable only), and its lifecycle is not
 * `deprecated` (a fallback, never latest).
 */
export function latestEligible(
  key: string,
  geography: InferenceGeography,
  layers: CatalogueLayers = CATALOGUE,
): boolean {
  const profile = profileIn(key, geography, layers);
  if (!profile || layers.offerings[key]?.lifecycle.status === "deprecated") {
    return false;
  }
  const status = canonicalOf(key, layers)?.upstream.bedrock[profile]?.status;
  return status !== "alpha" && status !== "beta";
}

/**
 * A family's routable versions in a geography, in candidate order (D3): latest-eligible
 * versions newest first, then deprecated ones newest first. Alpha and beta routes are left
 * out (pins only, slice 3).
 */
export function familyVersions(
  familyId: string,
  geography: InferenceGeography,
  layers: CatalogueLayers = CATALOGUE,
): OfferingKey[] {
  const keys = (Object.keys(layers.offerings) as OfferingKey[])
    .filter(
      (key) =>
        familyIdOf(key, layers) === familyId &&
        profileIn(key, geography, layers) !== undefined &&
        layers.offerings[key]?.retired !== true,
    )
    .sort((a, b) => compareVersionsNewestFirst(a, b, layers));
  const eligible = (key: string) => latestEligible(key, geography, layers);
  const deprecated = (key: string) => layers.offerings[key]?.lifecycle.status === "deprecated";
  return [...keys.filter(eligible), ...keys.filter((key) => !eligible(key) && deprecated(key))];
}

/** The routes of catalogue keys in a geography, in order (keys without a route there skipped). */
export function routesOf(keys: readonly string[], geography: InferenceGeography): string[] {
  return keys.flatMap((key) => {
    const profile = profileIn(key, geography);
    return profile ? [profile] : [];
  });
}

/**
 * A role's candidate offerings in one geography, in fallback order (D4): the effort's family's
 * versions (newest first), then its neighbour family's, never another lab's. `vision` runs on
 * Fast's family, then Balanced's, keeping image-capable versions only. The capability gate
 * (D4, Q18b) drops every version that cannot serve the role (`scope.capable`, else the
 * catalogue's fields), whatever the effort map says.
 */
export function deriveCandidates(
  role: ModelRole,
  scope: DerivationScope,
  geography: InferenceGeography,
): OfferingKey[] {
  const families: FamilyId[] = [];
  if (role === "vision") {
    for (const tier of ["fast", "balanced"] as const) {
      const family = effortFamily(tier, scope);
      if (family) {
        families.push(family);
      }
    }
  } else {
    const tier = ROLE_EFFORT_TIER[role];
    const family = effortFamily(tier, scope);
    if (family) {
      families.push(family);
      const neighbour = neighbourFamily(family, tier);
      if (neighbour) {
        families.push(neighbour);
      }
    }
  }
  const keys = families.flatMap((family) => familyVersions(family, geography));
  const unique = [...new Set(keys)];
  const capable = scope.capable ?? ((key: OfferingKey) => meetsRoleNeeds(key, role));
  return unique.filter(
    (key) => capable(key, role) && (role !== "vision" || scope.acceptsImages(key)),
  );
}

/** The default scope: every tenant's default labs (`INSTANCE_DEFAULT_LABS`), no family overrides. */
export function defaultDerivationScope(
  acceptsImages: (key: OfferingKey) => boolean,
): DerivationScope {
  return { labs: [...DEFAULT_LABS], effortFamilies: {}, acceptsImages };
}

/**
 * `models.allowedModels` entries expanded (D4, Q4): a family ID allows every version of the
 * family, current and future; a model key stands for itself. Unknown entries are dropped.
 */
export function expandAllowedModels(entries: readonly string[]): OfferingKey[] {
  const set = new Set(entries);
  return (Object.keys(BEDROCK_OFFERINGS) as OfferingKey[]).filter((key) => {
    const family = familyIdOf(key);
    return set.has(key) || (family !== undefined && set.has(family));
  });
}

/**
 * Whether a stored `models.allowedModels` names only versions the catalogue keeps as older
 * (model-picking D12, 5.10b): no family entry and no newest version of its family in any served
 * geography, e.g. a list of Claude 4.x keys left from the dev stopgap. Such a list never follows
 * a new version; the Models screen tells the admin to re-pick. Unknown entries are ignored; an
 * empty or unknown-only list is not flagged.
 */
export function namesOnlyOlderVersions(entries: readonly string[]): boolean {
  const known = entries.filter((e) => isFamilyId(e) || familyIdOf(e) !== undefined);
  if (known.length === 0 || known.some((e) => isFamilyId(e))) {
    return false;
  }
  return known.every((key) => {
    const family = familyIdOf(key);
    return (
      family !== undefined && SERVED_GEOGRAPHIES.every((g) => familyVersions(family, g)[0] !== key)
    );
  });
}
